import { useEffect, useState } from "react";
import { Button, Col, Form, FormControl, Row } from "react-bootstrap";
import {
    changePassword,
    getMe,
    renameHouse,
    sendVerificationEmail,
    updateEmailPreferences,
    updateProfile,
} from "../../services/ledgerService";
import "./Profile.css";

// ProfilePage is the self-serve account page (US-013). Account Info is
// read-only; the Name, House, Password, and Email Preferences sections are each
// self-contained components that load their current value from GET /api/me and
// save via their own endpoint (US-004..US-007) with success/error feedback.

const AUTH_METHOD_LABELS = {
    basic: "Email & password",
    oidc: "Google",
};

// formatJoinDate renders an ISO timestamp as a full-month date with no weekday
// (e.g. "July 1, 2026"), for the join-date pill.
function formatJoinDate(iso) {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
}

// Status renders the inline success/error feedback shared by every section.
function Status({ status }) {
    if (!status) return null;
    return (
        <p className={status.error ? "text-danger mt-2 mb-0" : "text-success mt-2 mb-0"}>
            {status.message}
        </p>
    );
}

// NameSection — edit first/last name (US-004, PATCH /api/profile).
function NameSection({ user }) {
    const [firstName, setFirstName] = useState(user.firstName || "");
    const [lastName, setLastName] = useState(user.lastName || "");
    const [status, setStatus] = useState(null);
    const [saving, setSaving] = useState(false);

    const handleSubmit = async (event) => {
        event.preventDefault();
        setSaving(true);
        setStatus(null);
        try {
            await updateProfile({ firstName, lastName });
            setStatus({ message: "Name updated.", error: false });
        } catch (err) {
            setStatus({ message: err.message, error: true });
        } finally {
            setSaving(false);
        }
    };

    const disabled = saving || firstName.trim() === "" || lastName.trim() === "";

    return (
        <section className="profile-section">
            <h2>Name</h2>
            <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-2" controlId="profile-first-name">
                    <Form.Label>First name</Form.Label>
                    <FormControl
                        type="text"
                        value={firstName}
                        maxLength={100}
                        onChange={(e) => setFirstName(e.target.value)}
                        required
                    />
                </Form.Group>
                <Form.Group className="mb-2" controlId="profile-last-name">
                    <Form.Label>Last name</Form.Label>
                    <FormControl
                        type="text"
                        value={lastName}
                        maxLength={100}
                        onChange={(e) => setLastName(e.target.value)}
                        required
                    />
                </Form.Group>
                <Button type="submit" variant="success" disabled={disabled}>
                    Save name
                </Button>
                <Status status={status} />
            </Form>
        </section>
    );
}

// HouseSection — rename the owned house (US-005, PUT /api/house).
function HouseSection({ user }) {
    const [name, setName] = useState(user.houseName || "");
    const [status, setStatus] = useState(null);
    const [saving, setSaving] = useState(false);

    const handleSubmit = async (event) => {
        event.preventDefault();
        setSaving(true);
        setStatus(null);
        try {
            const body = await renameHouse({ name });
            if (body && body.houseName) setName(body.houseName);
            setStatus({ message: "House name updated.", error: false });
        } catch (err) {
            setStatus({ message: err.message, error: true });
        } finally {
            setSaving(false);
        }
    };

    const disabled = saving || name.trim() === "";

    return (
        <section className="profile-section">
            <h2>House</h2>
            <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-2" controlId="profile-house-name">
                    <Form.Label>House name</Form.Label>
                    <FormControl
                        type="text"
                        value={name}
                        maxLength={100}
                        onChange={(e) => setName(e.target.value)}
                        required
                    />
                </Form.Group>
                <Button type="submit" variant="success" disabled={disabled}>
                    Save house name
                </Button>
                <Status status={status} />
            </Form>
        </section>
    );
}

// PasswordSection — change password for basic-auth users (US-006,
// POST /api/profile/password). OIDC users see a "managed by Google" notice.
function PasswordSection({ user }) {
    const [currentPassword, setCurrentPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [status, setStatus] = useState(null);
    const [saving, setSaving] = useState(false);

    if (user.authMethod === "oidc") {
        return (
            <section className="profile-section">
                <h2>Password</h2>
                <p>Your password is managed by Google.</p>
            </section>
        );
    }

    const handleSubmit = async (event) => {
        event.preventDefault();
        setStatus(null);
        if (newPassword.length < 8) {
            setStatus({ message: "New password must be at least 8 characters.", error: true });
            return;
        }
        if (newPassword !== confirmPassword) {
            setStatus({ message: "New passwords do not match.", error: true });
            return;
        }
        setSaving(true);
        try {
            await changePassword({ currentPassword, newPassword });
            setStatus({ message: "Password updated.", error: false });
            setCurrentPassword("");
            setNewPassword("");
            setConfirmPassword("");
        } catch (err) {
            setStatus({ message: err.message, error: true });
        } finally {
            setSaving(false);
        }
    };

    const disabled =
        saving || currentPassword === "" || newPassword === "" || confirmPassword === "";

    return (
        <section className="profile-section">
            <h2>Password</h2>
            <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-2" controlId="profile-current-password">
                    <Form.Label>Current password</Form.Label>
                    <FormControl
                        type="password"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        required
                    />
                </Form.Group>
                <Form.Group className="mb-2" controlId="profile-new-password">
                    <Form.Label>New password</Form.Label>
                    <FormControl
                        type="password"
                        value={newPassword}
                        placeholder="at least 8 characters"
                        onChange={(e) => setNewPassword(e.target.value)}
                        required
                    />
                </Form.Group>
                <Form.Group className="mb-2" controlId="profile-confirm-password">
                    <Form.Label>Confirm new password</Form.Label>
                    <FormControl
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                    />
                </Form.Group>
                <Button type="submit" variant="success" disabled={disabled}>
                    Change password
                </Button>
                <Status status={status} />
            </Form>
        </section>
    );
}

// EmailPreferencesSection — marketing on/off toggle (US-007,
// PUT /api/profile/email-preferences).
function EmailPreferencesSection({ user }) {
    const [enabled, setEnabled] = useState(Boolean(user.marketingEmailsEnabled));
    const [status, setStatus] = useState(null);
    const [saving, setSaving] = useState(false);

    const handleToggle = async (event) => {
        const next = event.target.checked;
        setEnabled(next);
        setSaving(true);
        setStatus(null);
        try {
            const body = await updateEmailPreferences({ marketingEmailsEnabled: next });
            const saved =
                body && typeof body.marketingEmailsEnabled === "boolean"
                    ? body.marketingEmailsEnabled
                    : next;
            setEnabled(saved);
            setStatus({ message: "Email preferences updated.", error: false });
        } catch (err) {
            setEnabled(!next);
            setStatus({ message: err.message, error: true });
        } finally {
            setSaving(false);
        }
    };

    return (
        <section className="profile-section">
            <h2>Email Preferences</h2>
            <Form>
                <Form.Check
                    type="switch"
                    id="profile-marketing-emails"
                    label="Receive marketing & product update emails"
                    checked={enabled}
                    disabled={saving}
                    onChange={handleToggle}
                />
                <Status status={status} />
            </Form>
        </section>
    );
}

// EmailVerificationStatus renders a verified/unverified badge next to the email
// and, when unverified, a button that re-sends the verification email.
function EmailVerificationStatus({ user }) {
    const [sent, setSent] = useState(false);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState("");

    if (user.emailVerified) {
        return (
            <span style={{ color: "#1a7f37", fontWeight: 600, whiteSpace: "nowrap" }} title="Email verified">
                ✓ Verified
            </span>
        );
    }

    const resend = async () => {
        setSending(true);
        setError("");
        try {
            await sendVerificationEmail();
            setSent(true);
        } catch (e) {
            setError(e.message || "Couldn't send. Please try again.");
        } finally {
            setSending(false);
        }
    };

    return (
        <span style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
            <span style={{ color: "#8a6d00", fontWeight: 600 }} title="Email not verified">⚠ Unverified</span>
            {sent ? (
                <span style={{ color: "#1a7f37" }}>Verification email sent — check your inbox.</span>
            ) : (
                <Button size="sm" variant="outline-primary" onClick={resend} disabled={sending}>
                    {sending ? "Sending…" : "Verify email"}
                </Button>
            )}
            {error && <span style={{ color: "#b23a2e" }}>{error}</span>}
        </span>
    );
}

export default function ProfilePage() {
    const [me, setMe] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        let active = true;
        getMe()
            .then((data) => {
                if (active) setMe(data);
            })
            .catch((err) => {
                console.error("Error loading profile:", err);
                if (active) setError("Unable to load your profile. Please try again.");
            });
        return () => {
            active = false;
        };
    }, []);

    if (error) {
        return (
            <Row className="profile">
                <Col xs={12} md={8} className="mx-auto mt-5 p-3">
                    <p className="text-danger">{error}</p>
                </Col>
            </Row>
        );
    }

    if (!me) {
        return (
            <Row className="profile">
                <Col xs={12} md={8} className="mx-auto mt-5 p-3">
                    <p>Loading...</p>
                </Col>
            </Row>
        );
    }

    const user = me.user || {};
    const authLabel = AUTH_METHOD_LABELS[user.authMethod] || user.authMethod || "—";

    return (
        <Row className="profile">
            <Col xs={12} md={8} className="mx-auto mt-5 p-3">
                <h1>Profile</h1>

                {/* Account Info — read-only. */}
                <section className="profile-section">
                    <h2>Account Info</h2>
                    <dl className="profile-info">
                        <dt>Email</dt>
                        <dd>
                            {user.email || "—"}{" "}
                            <EmailVerificationStatus user={user} />
                        </dd>
                        <dt>Sign-in method</dt>
                        <dd>
                            <span className="profile-pill profile-pill--auth">{authLabel}</span>
                        </dd>
                        <dt>Date joined</dt>
                        <dd>
                            {user.dateJoined ? (
                                <span className="profile-pill profile-pill--date">
                                    {formatJoinDate(user.dateJoined)}
                                </span>
                            ) : (
                                "—"
                            )}
                        </dd>
                    </dl>
                </section>

                <NameSection user={user} />
                <HouseSection user={user} />
                <PasswordSection user={user} />
                <EmailPreferencesSection user={user} />
            </Col>
        </Row>
    );
}
